import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Cookies from 'js-cookie';
import { useUserContext } from '../../../contexts/UserContext';
import styles from './GraphView.module.css';

// Sigma & Graphology
import { SigmaContainer, ControlsContainer, ZoomControl, FullScreenControl, useLoadGraph, useRegisterEvents, useSigma } from '@react-sigma/core';
import '@react-sigma/core/lib/style.css';
import Graph from 'graphology';

const GRAPH_API = process.env.REACT_APP_GRAPH_API_URL || "https://chimpknows.com";

const TYPES_META = {
  job: { fill: '#7c4dff', stroke: '#512da8', light: '#ede7f6', emoji: '💼', label: 'Jobs' },
  course: { fill: '#00acc1', stroke: '#006978', light: '#e0f7fa', emoji: '🎓', label: 'Courses' },
  event: { fill: '#2e7d32', stroke: '#1b5e20', light: '#e8f5e9', emoji: '🗓️', label: 'Events' },
  news: { fill: '#ef6c00', stroke: '#bf360c', light: '#fff3e0', emoji: '📰', label: 'News' },
}
const TYPE_ORDER = ['job', 'course', 'event', 'news']

const DEFAULT_FILTERS = {
  types: { job: true, course: true, event: true, news: true },
  minMatch: 0,
  shortlistedOnly: false,
  clustersOnly: false,
};

// Helper to determine dominant type of a cluster
const getDominantType = (item) => {
  if (item.type) return item.type; // It's an item
  // It's a cluster
  const counts = [
    { type: 'job', count: item.jobs_count || 0 },
    { type: 'course', count: item.courses_count || 0 },
    { type: 'event', count: item.events_count || 0 },
    { type: 'news', count: item.news_count || 0 }
  ];
  counts.sort((a, b) => b.count - a.count);
  return counts[0].count > 0 ? counts[0].type : 'job';
};

// ── WebGL Controller Component ──────────────────────────────────────────────
function GraphDataController({ data, viewState, userName, filters, setHoveredNode, setSelectedNode, user, currentClusterNode }) {
  const loadGraph = useLoadGraph();
  const registerEvents = useRegisterEvents();
  const sigma = useSigma();

  useEffect(() => {
    if (!data) return;
    const graph = new Graph({ multi: true });

    // 1. Gather all nodes (clusters or items)
    let apiNodes = [];
    if (viewState === 'CLUSTER') {
      let items = data.items || [];
      let subclusters = data.subclusters || [];

      // If we zoomed into a specific SUBCLUSTER, filter the items!
      if (currentClusterNode && currentClusterNode.parsed_subcluster_id) {
        const targetSubId = String(currentClusterNode.parsed_subcluster_id);
        items = items.filter(item => String(item.subcluster_id) === targetSubId);
        subclusters = []; // Hide other sibling subclusters when focused on a specific one
      }

      apiNodes = items.concat(subclusters);
    } else if (viewState === 'LOCAL') {
      apiNodes = data.nodes || [];
    }

    // 2. Filter nodes
    const filteredNodes = apiNodes.filter(n => {
      const isCluster = n.kind === 'cluster' || n.kind === 'subcluster';
      const type = getDominantType(n);

      // Strict Type Toggling dictates all nodes including clusters
      if (!filters.types[type]) return false;

      if (filters.shortlistedOnly) {
        if (isCluster) return false;
        if (!n.is_shortlisted) return false;
      }

      if (filters.clustersOnly) {
        if (!isCluster) return false;
      } else {
        if (!isCluster && filters.minMatch > 0 && ((n.match_percent || 0) < filters.minMatch)) return false;
      }

      return true;
    });

    const isClusterView = viewState === 'CLUSTER' && currentClusterNode;
    const centerNodeId = isClusterView ? `center-${currentClusterNode.id || currentClusterNode.cluster_id}` : '__user__';
    const centerNodeLabel = isClusterView ? (currentClusterNode.title || currentClusterNode.label) : (userName || 'You');
    const centerNodeKind = isClusterView ? 'cluster_center' : 'user';
    const centerNodeColor = isClusterView ? (TYPES_META[getDominantType(currentClusterNode)]?.stroke || '#432C5B') : '#432C5B';

    // Place the Center Node (User in OVERVIEW/LOCAL, Cluster in CLUSTER view)
    graph.addNode(centerNodeId, {
      x: 0,
      y: 0,
      size: 15,
      color: centerNodeColor,
      label: centerNodeLabel,
      kind: centerNodeKind,
      rawData: isClusterView ? { ...currentClusterNode, kind: 'cluster_center' } : { kind: 'user', title: userName, description: user?.user?.email, company: user?.user?.role || 'Member' }
    });

    const nodeIds = new Set([centerNodeId]);

    // Draw Dotted Ring Boundary for Shortlisted Zone
    const numDots = 60;
    const boundaryRadius = 200; // Encompasses orbit 1 (90 to 170)
    for (let i = 0; i < numDots; i++) {
      const dotAngle = (i / numDots) * Math.PI * 2;
      const bId = `boundary-dot-${i}`;
      nodeIds.add(bId);
      graph.addNode(bId, {
        x: Math.cos(dotAngle) * boundaryRadius,
        y: Math.sin(dotAngle) * boundaryRadius,
        size: 2,
        color: 'rgba(124, 77, 255, 0.5)',
        label: i === 0 ? '⭐ Shortlisted Zone' : '',
        kind: 'boundary'
      });
    }

    filteredNodes.forEach(node => {
      const type = getDominantType(node);

      let color = TYPES_META[type]?.fill || '#888';
      let title = node.title || node.label;

      // Calculate scaling based on node type
      let finalSize = 5;
      if (node.kind === 'cluster') {
        // Change back to bright categoric fill color so clusters mirror items
        color = TYPES_META[type]?.fill || '#555';
        // Shrunk cluster size to 10-15 to look cleanly proportioned against size-9 items
        finalSize = Math.max(10, Math.min(15, Math.log10(node.count || 10) * 5));
        title = `🌌 ${title}`; // Visual distinction for clusters
      } else if (node.kind === 'subcluster') {
        color = TYPES_META[type]?.fill || '#555';
        finalSize = Math.max(8, Math.min(12, Math.log10(node.count || 10) * 4));
        title = `✨ ${title}`; // Visual distinction for subclusters
      } else {
        finalSize = node.is_shortlisted ? 9 : 5;
      }

      // Gravity Math: Center is User at (0,0). Distance = Relevance. Angle = PCA clusters.
      let pcaX = typeof node.x === 'number' ? node.x : (Math.random() - 0.5) * 1000;
      let pcaY = typeof node.y === 'number' ? node.y : (Math.random() - 0.5) * 1000;

      // Preserve grouping by mapping raw x,y to an angle from center
      let angle = Math.atan2(pcaY, pcaX);
      if (isNaN(angle)) angle = Math.random() * Math.PI * 2;

      // High angular jitter for massive clusters prevents text and body overlap. Tiny jitter for regular items.
      if (node.kind === 'cluster' || node.kind === 'subcluster') {
        angle += (Math.random() - 0.5) * 1.5;
      } else {
        angle += (Math.random() - 0.5) * 0.15;
      }

      let radius = 800;
      const matchScore = node.match_percent || 0;

      if (node.kind === 'cluster' || node.kind === 'subcluster') {
        // Push major clusters completely outside the core items into the distant 800-1100 rim
        radius = 800 + Math.random() * 300;
      } else if (node.is_shortlisted) {
        // Orbit 1: Closest to Center
        radius = 90 + Math.random() * 80;
      } else if (matchScore >= 90) {
        // Orbit 2: High Match
        radius = 180 + (100 - matchScore) * 12 + Math.random() * 50;
      } else if (matchScore >= 70) {
        // Orbit 3: Mid Match
        radius = 350 + (90 - matchScore) * 15 + Math.random() * 80;
      } else {
        // Orbit 4: Low Match / Outskirts
        radius = 700 + Math.random() * 400;
      }

      const gravityX = Math.cos(angle) * radius;
      const gravityY = Math.sin(angle) * radius;

      nodeIds.add(node.id || `node-${node.cluster_id}`);

      graph.addNode(node.id || `node-${node.cluster_id}`, {
        x: gravityX,
        y: gravityY,
        size: finalSize,
        color: color,
        label: title,
        rawData: node,
        kind: node.kind || node.type // Using kind instead of type to prevent WebGL crashes
      });

      // Make a visual connection line from items to the center cluster when inside a cluster view
      if (isClusterView) {
        graph.addEdge(centerNodeId, node.id || `node-${node.cluster_id}`, {
          color: 'rgba(124, 77, 255, 0.08)',
          size: 0.5
        });
      }
    });

    // 3. Add Edges exclusively inside drilled-down Cluster Views
    if (viewState === 'CLUSTER') {
      (data.edges || []).forEach((e) => {
        const src = e.src || e.source;
        const dst = e.dst || e.target;
        if (nodeIds.has(src) && nodeIds.has(dst)) {
          graph.addEdge(src, dst, {
            color: 'rgba(124, 77, 255, 0.15)',
            size: Math.max(0.2, (e.weight || 0.2) * 2)
          });
        }
      });
    }

    loadGraph(graph);

    // Animate camera to see the full layout
    setTimeout(() => {
      sigma.getCamera().animatedReset({ duration: 800 });
    }, 100);

  }, [data, viewState, userName, filters, loadGraph, sigma, user, currentClusterNode]);

  // Click & Hover Events
  useEffect(() => {
    registerEvents({
      enterNode: (e) => {
        setHoveredNode(sigma.getGraph().getNodeAttribute(e.node, 'rawData'));
      },
      leaveNode: (e) => setHoveredNode(null),
      clickNode: (e) => {
        setSelectedNode(sigma.getGraph().getNodeAttribute(e.node, 'rawData'));
      }
    });
  }, [registerEvents, sigma, setHoveredNode, setSelectedNode]);

  return null;
}

// ── Main View Component ───────────────────────────────────────────────────
export default function GraphView() {
  const { user } = useUserContext();
  const userId = user?.user?.user_id;
  const userName = user?.user?.name || [user?.user?.first_name, user?.user?.last_name].filter(Boolean).join(' ') || 'You';

  // View States: CLUSTER | LOCAL
  const [viewState, setViewState] = useState('LOCAL');
  const [currentClusterId, setCurrentClusterId] = useState(null);
  const [currentClusterNode, setCurrentClusterNode] = useState(null);

  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);

  // Filters
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [showFilters, setShowFilters] = useState(false);

  // Maintain breadcrumb trace essentially for zooming back up
  const [clusterHistory, setClusterHistory] = useState([]);

  const fetchGraphData = useCallback(async (view, clusterId = null, clusterDataNode = null) => {
    if (!userId) { setError('User not loaded.'); return; }
    setLoading(true); setError(null); setSelectedNode(null);
    try {
      const token = Cookies.get('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      let endpoint = '';
      let params = new URLSearchParams({ user_id: userId, version: 'v2_main50' });
      let payload;

      if (view === 'CLUSTER') {
        endpoint = `${GRAPH_API}/api/graph/cluster/${clusterId}?${params}&include_items=true`;
        const res = await fetch(endpoint, { headers });
        if (!res.ok) throw new Error(`Server ${res.status}`);
        payload = await res.json();
      } else if (view === 'LOCAL') {
        // Fetch both Overview (Clusters) and Subgraph (Items)
        const overviewRes = await fetch(`${GRAPH_API}/api/graph/overview?${params}`, { headers });
        const localRes = await fetch(`${GRAPH_API}/api/graph/subgraph?${new URLSearchParams({ user_id: userId, limit_nodes: 1000, limit_edges: 2000 })}`, { headers });

        if (!overviewRes.ok || !localRes.ok) throw new Error(`Server errors loading network data.`);

        const overviewPayload = await overviewRes.json();
        const localPayload = await localRes.json();

        // Merge nodes and edges so both highly relevant generic items and massive cluster suns are shown together
        payload = {
          nodes: [...(overviewPayload.nodes || []), ...(localPayload.nodes || [])],
          edges: [...(overviewPayload.edges || []), ...(localPayload.edges || [])]
        };
      }

      setGraphData(payload);
      setViewState(view);
      setCurrentClusterId(clusterId);
      if (clusterDataNode) setCurrentClusterNode(clusterDataNode);
    } catch (e) {
      setError(e.message || 'Failed to fetch graph data');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Initial Load
  useEffect(() => {
    if (userId) fetchGraphData('LOCAL');
  }, [userId, fetchGraphData]);

  const handleZoomIntoCluster = (clusterNode) => {
    // ID could be 'cluster:33' or 'subcluster:33:2' or '33' or '33:2'
    const rawId = String(clusterNode.id || clusterNode.cluster_id || '').replace(/^(cluster:|subcluster:)/, '');

    // Split by colon to separate main cluster ID from subcluster ID
    const parts = rawId.split(':');
    const mainClusterId = parts[0];
    const subClusterId = parts.length > 1 ? parts[1] : null;

    // Save history so user can go back to parent cluster or top level
    if (viewState === 'CLUSTER' && currentClusterId) {
      setClusterHistory(prev => [...prev, { id: currentClusterId, node: currentClusterNode }]);
    }

    const activeNode = { ...clusterNode, parsed_subcluster_id: subClusterId };
    setCurrentClusterNode(activeNode);

    // We only pass the main integer ID to the backend fetch!
    fetchGraphData('CLUSTER', mainClusterId, activeNode);
  };

  const handleZoomBackOut = () => {
    // If we have history, pop it and go to the parent cluster!
    if (clusterHistory.length > 0) {
      const historyCopy = [...clusterHistory];
      const prev = historyCopy.pop();
      setClusterHistory(historyCopy);
      fetchGraphData('CLUSTER', prev.id, prev.node);
    } else {
      // Otherwise return entirely to top level LOCAL view
      fetchGraphData('LOCAL');
    }
  };

  return (
    <div className={styles.container}>
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className={styles.headerBar}>
        <span className={styles.graphTitle}>🌸 Discovery Universe</span>
        <div className={styles.typePills}>
          {TYPE_ORDER.map(t => {
            const m = TYPES_META[t];
            return (
              <span key={t} className={styles.typePill} style={{ background: m.light, color: m.fill }}>
                {m.emoji} {m.label}
              </span>
            );
          })}
        </div>

        {viewState === 'CLUSTER' && (
          <button className={styles.backBtn} onClick={handleZoomBackOut}>← Back Out</button>
        )}

        <button className={`${styles.filterToggleBtn} ${showFilters ? styles.active : ''}`}
          onClick={() => setShowFilters(v => !v)}>
          <span>⚙️</span>
          <span className={styles.filterToggleText}>Filters</span>
        </button>
        <button className={styles.refreshBtn} onClick={() => fetchGraphData(viewState, currentClusterId, currentClusterNode)} disabled={loading}>🔄</button>
      </div>

      {/* ── Filter Panel ──────────────────────────────────────────────── */}
      {showFilters && (
        <div className={styles.filterPanel}>
          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Types</span>
            <div className={styles.typeToggles}>
              {TYPE_ORDER.map(type => {
                const m = TYPES_META[type];
                return (
                  <button key={type}
                    className={`${styles.typeToggle} ${filters.types[type] ? styles.typeActive : ''}`}
                    style={filters.types[type] ? { background: m.light, borderColor: m.fill, color: m.fill } : {}}
                    onClick={() => setFilters(f => ({ ...f, types: { ...f.types, [type]: !f.types[type] } }))}>
                    {m.emoji} {m.label}
                  </button>
                )
              })}
            </div>
          </div>
          <div className={styles.filterDivider} />
          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Min match: <strong>{filters.minMatch}%</strong></span>
            <input type="range" min="0" max="100" step="5" value={filters.minMatch}
              onChange={e => setFilters(f => ({ ...f, minMatch: +e.target.value }))}
              className={styles.slider} />
          </div>
          <div className={styles.filterGroup}>
            <label className={styles.checkboxLabel}>
              <input type="checkbox" checked={filters.shortlistedOnly}
                onChange={e => setFilters(f => ({ ...f, shortlistedOnly: e.target.checked, clustersOnly: false }))} />
              ⭐ Shortlisted only
            </label>
            <label className={styles.checkboxLabel} style={{ marginLeft: '12px' }}>
              <input type="checkbox" checked={filters.clustersOnly}
                onChange={e => setFilters(f => ({ ...f, clustersOnly: e.target.checked, shortlistedOnly: false }))} />
              🌌 Clusters only
            </label>
          </div>
          <button className={styles.resetBtn} onClick={() => setFilters(DEFAULT_FILTERS)}>↺ Reset</button>
        </div>
      )}

      {/* ── Main Layout ───────────────────────────────────────────────── */}
      <div className={styles.canvasRow}>
        <div className={styles.canvasWrap}>
          {loading && (
            <div className={styles.overlay}>
              <div className={styles.spinner} />
              <p className={styles.overlayText}>Calculating trajectories...</p>
            </div>
          )}
          {error && !loading && (
            <div className={styles.overlay}>
              <p className={styles.errorMsg}>⚠️ {error}</p>
              <button className={styles.retryBtn} onClick={() => fetchGraphData(viewState, currentClusterId, currentClusterNode)}>Retry</button>
            </div>
          )}

          {/* WebGL Sigma Container */}
          <SigmaContainer
            style={{ width: "100%", height: "100%", background: "transparent" }}
            settings={{
              labelDensity: 0.02,
              labelGridCellSize: 100,
              labelRenderedSizeThreshold: 22,
              defaultNodeColor: "#aaaaaa",
              defaultEdgeColor: "rgba(124,77,255,0.1)",
              hideEdgesOnMove: true
            }}
          >
            <ControlsContainer position={"bottom-right"}>
              <ZoomControl />
              <FullScreenControl />
            </ControlsContainer>

            <GraphDataController
              data={graphData}
              viewState={viewState}
              userName={userName}
              filters={filters}
              setHoveredNode={setHoveredNode}
              setSelectedNode={setSelectedNode}
              user={user}
              currentClusterNode={currentClusterNode}
            />
          </SigmaContainer>
        </div>

        {/* ── Side Panel ────────────────────────────────────────────────── */}
        {selectedNode ? (
          <div className={styles.sidePanel}>
            <div className={styles.sidePanelHeader}>
              <span style={{ fontSize: 20 }}>
                {selectedNode.kind === 'user' ? '👑' : (selectedNode.kind === 'cluster_center' ? '🌌' : (TYPES_META[getDominantType(selectedNode)]?.emoji || '⭐'))}
              </span>
              <span className={styles.sidePanelType} style={{ color: selectedNode.kind === 'user' ? '#432C5B' : (TYPES_META[getDominantType(selectedNode)]?.fill || '#888') }}>
                {((selectedNode.kind === 'cluster_center' ? 'Cluster Core' : selectedNode.kind) || selectedNode.type || 'unknown').toUpperCase()}
              </span>
              <button className={styles.closePanelBtn} onClick={() => setSelectedNode(null)}>✕</button>
            </div>

            {selectedNode.kind === 'user' ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '10px 0' }}>
                <div style={{ background: 'linear-gradient(135deg, #7c4dff, #512da8)', color: 'white', width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '36px', marginBottom: '15px', boxShadow: '0 8px 16px rgba(124, 77, 255, 0.3)' }}>
                  👤
                </div>
                <h3 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0 0 5px 0', color: '#1a1a1a' }}>{userName}</h3>
                <p style={{ color: '#666', margin: '0 0 15px 0', fontSize: '14px' }}>✉️ {user?.user?.email}</p>
                <div style={{ background: '#ede7f6', color: '#7c4dff', padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 'bold', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  🏢 {user?.user?.role || 'Member'}
                </div>

                <div style={{ display: 'flex', gap: '15px', marginTop: '30px', width: '100%' }}>
                  <div style={{ flex: 1, background: 'rgba(124, 77, 255, 0.05)', padding: '15px', borderRadius: '12px', border: '1px solid rgba(124, 77, 255, 0.1)' }}>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: '#512da8' }}>{graphData?.nodes?.length || 0}</div>
                    <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '4px' }}>Total Nodes</div>
                  </div>
                  <div style={{ flex: 1, background: 'rgba(124, 77, 255, 0.05)', padding: '15px', borderRadius: '12px', border: '1px solid rgba(124, 77, 255, 0.1)' }}>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: '#512da8' }}>{filters.minMatch}%</div>
                    <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '4px' }}>Match Filter</div>
                  </div>
                </div>

                <div style={{ marginTop: '25px', padding: '15px', background: 'linear-gradient(135deg, rgba(124, 77, 255, 0.05), rgba(0, 172, 193, 0.05))', borderRadius: '12px', fontSize: '13px', lineHeight: '1.6', color: '#444', borderLeft: '3px solid #7c4dff', textAlign: 'left' }}>
                  <strong>🌌 Center of the Universe</strong><br />
                  Nodes orbit you based on proximity algorithms. The closer an item is to the center, the more relevant it is to your professional profile.
                </div>
              </div>
            ) : (
              <>
                <h3 className={styles.sidePanelTitle}>{selectedNode.label || selectedNode.title}</h3>
                {selectedNode.description && <p className={styles.sidePanelMeta}>{selectedNode.description}</p>}
                {selectedNode.company && <p className={styles.sidePanelMeta}>🏢 {selectedNode.company}</p>}

                {/* Cluster Stats */}
                {(selectedNode.kind === 'cluster' || selectedNode.kind === 'subcluster') && (
                  <div className={styles.clusterStats}>
                    <p><strong>Total Items:</strong> {selectedNode.count?.toLocaleString()}</p>
                    {selectedNode.jobs_count > 0 && <p>💼 Jobs: {selectedNode.jobs_count?.toLocaleString()}</p>}
                    {selectedNode.courses_count > 0 && <p>🎓 Courses: {selectedNode.courses_count?.toLocaleString()}</p>}

                    <button className={styles.drillDownBtn} onClick={() => handleZoomIntoCluster(selectedNode)}>
                      🔍 Dive into {selectedNode.kind === 'subcluster' ? 'Sub-Cluster' : 'Cluster'}
                    </button>
                  </div>
                )}

                {/* Sub-Cluster/Center Stats */}
                {selectedNode.kind === 'cluster_center' && (
                  <div className={styles.clusterStats}>
                    <p><strong>Total Items:</strong> {selectedNode.count?.toLocaleString()}</p>
                    <p>You are viewing the interior of this cluster.</p>
                    <p>Its contents are gravitationally orbiting it based on relevance.</p>
                  </div>
                )}

                {/* Individual Item Stats */}
                {selectedNode.match_percent != null && (
                  <div className={styles.matchBadge} style={{ background: '#ede7f6' }}>
                    <span className={styles.matchNum} style={{ color: '#7c4dff' }}>{selectedNode.match_percent}%</span>
                    <span className={styles.matchLbl}>Match</span>
                  </div>
                )}
                {selectedNode.is_shortlisted && (
                  <div className={styles.shortlistedTag} style={{ marginTop: '5px' }}>⭐ Shortlisted</div>
                )}

                {selectedNode.bullets?.length > 0 && (
                  <ul className={styles.bullets}>
                    {selectedNode.bullets.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                )}
              </>
            )}
          </div>
        ) : (
          <div className={styles.sidePanelEmpty}>
            <div className={styles.sidePanelEmptyIcon}>✨</div>
            <p>Click any node or cluster for details</p>
          </div>
        )}
      </div>

      {hoveredNode && !selectedNode && (
        <div className={styles.tooltipFixed}>
          <strong>{hoveredNode.label || hoveredNode.title}</strong>
          {hoveredNode.count && <span> ({hoveredNode.count} items)</span>}
          {hoveredNode.match_percent && <span> · {hoveredNode.match_percent}% match</span>}
        </div>
      )}
    </div>
  );
}
